/**
 * EvaluationGridManagement — Admin
 * Gestion des grilles d'évaluation pour l'analyse CV.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import {
  BarChart2, Plus, Edit, Trash2, Check, X,
  ChevronDown, ChevronRight, Star, GripVertical,
} from 'lucide-react';
import {
  getEvaluationGrids, createEvaluationGrid, updateEvaluationGrid, deleteEvaluationGrid,
} from '../../api';

const EMPTY_CRITERION = { name: '', maxPoints: 10, scoringGuidelines: '' };

const EvaluationGridManagement = () => {
  const [grids, setGrids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGrids, setExpandedGrids] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '', position: '', isDefault: false, criteria: [{ ...EMPTY_CRITERION }],
  });
  const [saving, setSaving] = useState(false);

  const fetchGrids = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEvaluationGrids();
      setGrids(res.data || []);
      setError(null);
    } catch {
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGrids(); }, [fetchGrids]);

  const toggleGrid = (id) => setExpandedGrids(prev => ({ ...prev, [id]: !prev[id] }));

  const openCreate = () => {
    setEditingId(null);
    setFormData({ name: '', position: '', isDefault: false, criteria: [{ ...EMPTY_CRITERION }] });
    setShowForm(true);
    setError(null);
  };

  const openEdit = (grid) => {
    setEditingId(grid.id);
    setFormData({
      name: grid.name,
      position: grid.position || '',
      isDefault: grid.isDefault || false,
      criteria: grid.criteria?.length
        ? grid.criteria.map(c => ({ name: c.name, maxPoints: c.maxPoints, scoringGuidelines: c.scoringGuidelines || '' }))
        : [{ ...EMPTY_CRITERION }],
    });
    setShowForm(true);
    setError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { setError('Le nom est requis'); return; }
    if (!formData.criteria.length) { setError('Au moins un critère requis'); return; }
    for (const c of formData.criteria) {
      if (!c.name.trim()) { setError('Tous les critères doivent avoir un nom'); return; }
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        position: formData.position.trim() || null,
        isDefault: formData.isDefault,
        criteria: formData.criteria.map(c => ({
          name: c.name.trim(),
          maxPoints: parseInt(c.maxPoints) || 10,
          scoringGuidelines: c.scoringGuidelines.trim() || null,
        })),
      };
      if (editingId) {
        await updateEvaluationGrid(editingId, payload);
      } else {
        await createEvaluationGrid(payload);
      }
      setShowForm(false);
      fetchGrids();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur d\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette grille d\'évaluation ?')) return;
    try {
      await deleteEvaluationGrid(id);
      fetchGrids();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de suppression');
    }
  };

  // Gestion des critères dans le formulaire
  const addCriterion = () => setFormData(f => ({ ...f, criteria: [...f.criteria, { ...EMPTY_CRITERION }] }));
  const removeCriterion = (idx) => setFormData(f => ({ ...f, criteria: f.criteria.filter((_, i) => i !== idx) }));
  const updateCriterion = (idx, field, value) =>
    setFormData(f => ({ ...f, criteria: f.criteria.map((c, i) => i === idx ? { ...c, [field]: value } : c) }));

  const totalMax = (criteria) => criteria?.reduce((s, c) => s + (parseInt(c.maxPoints) || 0), 0) || 0;

  const inputCls = 'border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 w-full';

  return (
<div className="px-4 sm:px-6 lg:px-8 space-y-6"><Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <BarChart2 size={16} className="text-primary" />
            Grilles d'évaluation CV
          </span>
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 text-sm bg-primary text-white px-3 py-1.5 rounded-lg hover:bg-primary-dark transition"
          >
            <Plus size={14} />Ajouter
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">{error}</div>
        )}

        {/* Formulaire ajout / modification */}
        {showForm && (
          <form onSubmit={handleSave} className="mb-5 p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-4">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-gray-800 text-sm">
                {editingId ? 'Modifier la grille' : 'Nouvelle grille d\'évaluation'}
              </h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>

            {/* Infos grille */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nom de la grille *</label>
                <input className={inputCls} value={formData.name} required
                  onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ex: Grille EJE standard" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Poste ciblé (optionnel)</label>
                <input className={inputCls} value={formData.position}
                  onChange={e => setFormData(f => ({ ...f, position: e.target.value }))}
                  placeholder="Ex: EJE, AP, Auxiliaire..." />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="rounded border-gray-300 text-primary"
                checked={formData.isDefault}
                onChange={e => setFormData(f => ({ ...f, isDefault: e.target.checked }))} />
              <span className="text-gray-700">Grille par défaut (utilisée automatiquement)</span>
            </label>

            {/* Critères */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Critères — Total : {totalMax(formData.criteria)} pts
                </span>
                <button type="button" onClick={addCriterion}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-medium">
                  <Plus size={12} />Ajouter un critère
                </button>
              </div>
              <div className="space-y-3">
                {formData.criteria.map((criterion, idx) => (
                  <div key={idx} className="flex gap-2 items-start p-3 bg-white rounded-lg border border-gray-200">
                    <GripVertical size={16} className="text-gray-300 mt-2 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="flex gap-2">
                        <input className={inputCls + ' flex-1'} placeholder="Nom du critère *"
                          value={criterion.name}
                          onChange={e => updateCriterion(idx, 'name', e.target.value)} />
                        <div className="w-24 flex-shrink-0">
                          <input className={inputCls} type="number" min="1" max="100"
                            placeholder="Pts" value={criterion.maxPoints}
                            onChange={e => updateCriterion(idx, 'maxPoints', e.target.value)} />
                        </div>
                      </div>
                      <textarea
                        className={inputCls + ' resize-none'}
                        rows={2}
                        placeholder="Règles de notation (ex: 8-10 = diplôme EJE, 5-7 = CAP, 0-4 = aucune formation)"
                        value={criterion.scoringGuidelines}
                        onChange={e => updateCriterion(idx, 'scoringGuidelines', e.target.value)}
                      />
                    </div>
                    <button type="button" onClick={() => removeCriterion(idx)}
                      className="text-gray-300 hover:text-red-400 mt-1 flex-shrink-0">
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <button type="submit" disabled={saving}
                className="flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-dark disabled:opacity-50">
                <Check size={14} />{saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Annuler
              </button>
            </div>
          </form>
        )}

        {/* Liste des grilles */}
        {loading ? (
          <p className="text-sm text-gray-400 text-center py-6">Chargement...</p>
        ) : grids.length === 0 ? (
          <div className="text-center py-8">
            <BarChart2 size={32} className="text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Aucune grille d'évaluation.</p>
            <p className="text-xs text-gray-400 mt-1">Créez une grille pour pouvoir analyser les CV.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {grids.map(grid => (
              <div key={grid.id} className="border border-gray-200 rounded-xl overflow-hidden">
                {/* En-tête grille */}
                <div
                  className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleGrid(grid.id)}
                >
                  {expandedGrids[grid.id]
                    ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
                    : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800 text-sm">{grid.name}</span>
                      {grid.isDefault && (
                        <span className="inline-flex items-center gap-0.5 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                          <Star size={10} />Par défaut
                        </span>
                      )}
                      {grid.position && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{grid.position}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {grid.criteria?.length || 0} critère{(grid.criteria?.length || 0) > 1 ? 's' : ''} — {totalMax(grid.criteria)} pts max
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <button onClick={() => openEdit(grid)} className="p-1.5 text-gray-400 hover:text-primary rounded-lg hover:bg-primary/5">
                      <Edit size={15} />
                    </button>
                    <button onClick={() => handleDelete(grid.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {/* Tableau des critères (expandable) */}
                {expandedGrids[grid.id] && grid.criteria?.length > 0 && (
                  <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left">
                          <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Critère</th>
                          <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide w-16 text-center">Pts max</th>
                          <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Règles de notation</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {grid.criteria.map((c, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 font-medium text-gray-800">{c.name}</td>
                            <td className="px-4 py-2.5 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-6 bg-primary/10 text-primary text-xs font-semibold rounded">
                                {c.maxPoints}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-gray-500 text-xs">
                              {c.scoringGuidelines || <span className="italic text-gray-300">—</span>}
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-primary/5 font-semibold">
                          <td className="px-4 py-2.5 text-primary text-sm">Total</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className="inline-flex items-center justify-center w-8 h-6 bg-primary text-white text-xs font-semibold rounded">
                              {totalMax(grid.criteria)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5" />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card></div>
  );
};

export default EvaluationGridManagement;
